/**
 * Bulk Actions Toolbar Component
 *
 * A toolbar that appears when items are selected in a DataTable,
 * providing bulk action capabilities.
 *
 * @example
 * <BulkActionsToolbar
 *   selectedCount={selectedIds.length}
 *   totalCount={data.length}
 *   onClearSelection={() => setSelectedIds([])}
 *   onSelectAll={() => setSelectedIds(data.map(d => d.id))}
 *   actions={[
 *     { label: 'Delete', icon: <DeleteIcon />, onClick: handleBulkDelete, variant: 'error' },
 *     { label: 'Export', icon: <ExportIcon />, onClick: handleExport },
 *   ]}
 * />
 */

import React from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Divider,
  Collapse,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  SelectAll as SelectAllIcon,
} from "@mui/icons-material";

export interface BulkAction {
  /** Action label */
  label: string;
  /** Action icon */
  icon?: React.ReactNode;
  /** Click handler, receives selected keys */
  onClick: () => void;
  /** Whether action is disabled */
  disabled?: boolean;
  /** Button variant for styling */
  variant?: "default" | "primary" | "error" | "warning";
  /** Tooltip text */
  tooltip?: string;
  /** Whether to show loading state */
  loading?: boolean;
}

export interface BulkActionsToolbarProps {
  /** Number of selected items */
  selectedCount: number;
  /** Total number of items (for "select all" text) */
  totalCount: number;
  /** Clear selection handler */
  onClearSelection: () => void;
  /** Select all handler (optional) */
  onSelectAll?: () => void;
  /** Available bulk actions */
  actions: BulkAction[];
  /** Whether the toolbar is visible (defaults to selectedCount > 0) */
  visible?: boolean;
  /** Entity name for display (e.g., "students", "invoices") */
  entityName?: string;
}

const variantColors: Record<NonNullable<BulkAction["variant"]>, string> = {
  default: "inherit",
  primary: "primary",
  error: "error",
  warning: "warning",
};

export const BulkActionsToolbar: React.FC<BulkActionsToolbarProps> = ({
  selectedCount,
  totalCount,
  onClearSelection,
  onSelectAll,
  actions,
  visible,
  entityName = "items",
}) => {
  const isVisible = visible ?? selectedCount > 0;
  const allSelected = selectedCount === totalCount && totalCount > 0;

  return (
    <Collapse in={isVisible}>
      <Paper
        role="toolbar"
        aria-label={`Bulk actions for ${selectedCount} selected ${entityName}`}
        elevation={2}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 2,
          py: 1,
          mb: 2,
          bgcolor: "primary.50",
          borderLeft: 4,
          borderColor: "primary.main",
        }}
      >
        {/* Selection count */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip
            label={selectedCount}
            color="primary"
            size="small"
            sx={{ fontWeight: 600 }}
          />
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            {entityName} selected
          </Typography>
        </Box>

        {/* Select all / Clear selection */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {onSelectAll && !allSelected && (
            <Tooltip title={`Select all ${totalCount} ${entityName}`}>
              <Button
                size="small"
                startIcon={<SelectAllIcon />}
                onClick={onSelectAll}
                sx={{ textTransform: "none" }}
              >
                Select all
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Clear selection">
            <IconButton
              size="small"
              onClick={onClearSelection}
              aria-label="Clear selection"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {/* Bulk actions */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexGrow: 1 }}>
          {actions.map((action, index) => (
            <Tooltip key={index} title={action.tooltip || action.label}>
              <span>
                <Button
                  size="small"
                  variant={action.variant === "primary" ? "contained" : "outlined"}
                  color={variantColors[action.variant || "default"] as "inherit" | "primary" | "error" | "warning"}
                  startIcon={action.icon}
                  onClick={action.onClick}
                  disabled={action.disabled || action.loading}
                  sx={{ textTransform: "none" }}
                >
                  {action.loading ? "Processing..." : action.label}
                </Button>
              </span>
            </Tooltip>
          ))}
        </Box>

        {/* Keyboard hint */}
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            display: { xs: "none", md: "block" }
          }}>
          Press <kbd>Esc</kbd> to clear
        </Typography>
      </Paper>
    </Collapse>
  );
};

export default BulkActionsToolbar;
